import { FunctionComponent, useState, useCallback, useRef, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useDispatch, useSelector } from 'react-redux';
import { DashboardWrapper, Card, Text, Button } from 'src/shared';
import styled from 'styled-components';
import { Colors } from 'src/utils/color';
import { Size } from 'src/shared/text';
import { submitHDF5Upload, pollJobStatus, resetUpload, selectUploadState } from 'src/reducers/upload';
import { presignUpload, submitJob } from 'src/services/api';
// Zip batch upload — will be implemented soon; uncomment when restoring:
// import { ingestZip, getIngestionStatus } from 'src/services/api';
import type { AppDispatch } from 'src/store';

const MAX_FILE_SIZE = 4 * 1024 * 1024 * 1024;
// const MAX_ZIP_SIZE = 5 * 1024 * 1024 * 1024; // zip upload (commented below)
const VALID_EXTENSIONS = ['.h5', '.hdf5'];

// ---------------------------------------------------------------------------
// Styled components
// ---------------------------------------------------------------------------
const Container = styled.div`
    display: flex;
    flex-direction: column;
    gap: 24px;
    padding: 24px;
    max-width: 720px;
`;

const BackButton = styled.button`
    background: none;
    border: 1px solid ${Colors.extra.light_gray};
    border-radius: 8px;
    padding: 8px 14px;
    cursor: pointer;
    font-size: 13px;
    color: ${Colors.extra.dark_gray};
    align-self: flex-start;
    &:hover { background: ${Colors.extra.light_gray}; }
`;

const TabBar = styled.div`
    display: flex;
    gap: 0;
    border-bottom: 2px solid ${Colors.extra.light_gray};
`;

const Tab = styled.button<{ $active: boolean }>`
    padding: 12px 24px;
    border: none;
    background: none;
    cursor: pointer;
    font-size: 14px;
    font-weight: ${p => p.$active ? 600 : 400};
    color: ${p => p.$active ? Colors.theme.primary : Colors.extra.dark_gray};
    border-bottom: 2px solid ${p => p.$active ? Colors.theme.primary : 'transparent'};
    margin-bottom: -2px;
    transition: all 0.15s ease;
    &:hover { color: ${Colors.theme.primary}; }
`;

const UploadCard = styled(Card)`
    padding: 24px;
`;

const UploadZone = styled.div<{ $dragOver?: boolean }>`
    border: 2px dashed ${p => p.$dragOver ? Colors.theme.primary : Colors.extra.light_gray};
    border-radius: 10px;
    padding: 40px;
    text-align: center;
    cursor: pointer;
    transition: all 0.2s ease;
    &:hover { border-color: ${Colors.theme.primary}; }
`;

const Constraints = styled.div`
    margin-top: 12px;
    padding: 12px;
    border-radius: 8px;
    background: ${Colors.extra.light_gray}55;
    font-size: 12px;
    color: ${Colors.extra.dark_gray};
    display: flex;
    gap: 16px;
    justify-content: center;
`;

const FileRow = styled.div`
    display: flex;
    align-items: center;
    justify-content: space-between;
    padding: 10px 12px;
    border: 1px solid ${Colors.extra.light_gray};
    border-radius: 8px;
    margin-top: 6px;
`;

const FileName = styled.div`
    font-weight: 500;
    font-size: 13px;
    color: ${Colors.extra.black};
`;

const FileSizeLabel = styled.div`
    font-size: 11px;
    color: ${Colors.extra.dark_gray};
`;

const StatusLabel = styled.span<{ $color: string }>`
    font-size: 11px;
    font-weight: 600;
    color: ${p => p.$color};
`;

const RemoveBtn = styled.button`
    background: none;
    border: none;
    color: #DC2626;
    cursor: pointer;
    font-size: 12px;
    &:hover { text-decoration: underline; }
`;

const ProgressBarBg = styled.div`
    width: 100%;
    height: 8px;
    border-radius: 4px;
    background: ${Colors.extra.light_gray};
    overflow: hidden;
`;

const ProgressBarFill = styled.div<{ $pct: number; $color?: string }>`
    height: 100%;
    width: ${p => Math.min(100, p.$pct)}%;
    background: ${p => p.$color || Colors.theme.primary};
    transition: width 0.3s ease;
`;

const ErrorBox = styled.div`
    padding: 12px;
    border-radius: 8px;
    background: #FEE2E2;
    color: #DC2626;
    font-size: 13px;
    margin-top: 12px;
`;

const SuccessBox = styled.div`
    padding: 16px;
    border-radius: 8px;
    background: #D1FAE5;
    color: #065F46;
    font-size: 13px;
    display: flex;
    flex-direction: column;
    gap: 8px;
    margin-top: 12px;
`;

const Summary = styled.div`
    font-size: 13px;
    color: ${Colors.extra.dark_gray};
    margin-top: 12px;
`;

const SectionTitle = styled.div`
    font-size: 14px;
    font-weight: 600;
    color: ${Colors.extra.black};
    margin-bottom: 12px;
`;

// ---------------------------------------------------------------------------
// Helpers
// ---------------------------------------------------------------------------
const fmt = (bytes: number) => {
    if (bytes === 0) return '0 B';
    const k = 1024;
    const s = ['B', 'KB', 'MB', 'GB'];
    const i = Math.floor(Math.log(bytes) / Math.log(k));
    return `${(bytes / k ** i).toFixed(1)} ${s[i]}`;
};

const validateHDF5 = (f: File): string | null => {
    const ext = f.name.substring(f.name.lastIndexOf('.')).toLowerCase();
    if (!VALID_EXTENSIONS.includes(ext)) return `Invalid type (${ext})`;
    if (f.size > MAX_FILE_SIZE) return `Too large (${(f.size / (1024 ** 3)).toFixed(1)} GB)`;
    return null;
};

// ---------------------------------------------------------------------------
// Batch file state
// ---------------------------------------------------------------------------
interface BatchFileState {
    file: File;
    status: 'pending' | 'uploading' | 'submitted' | 'failed';
    jobId: string | null;
    error: string | null;
    validationError: string | null;
}

// ---------------------------------------------------------------------------
// Component
// ---------------------------------------------------------------------------
const UploadPage: FunctionComponent = () => {
    const navigate = useNavigate();
    const dispatch = useDispatch<AppDispatch>();
    const upload = useSelector(selectUploadState);
    const [activeTab, setActiveTab] = useState<'single' | 'batch'>('single');

    // --- Single file state ---
    const [file, setFile] = useState<File | null>(null);
    const [validationError, setValidationError] = useState<string | null>(null);
    const [dragOver, setDragOver] = useState(false);
    const pollRef = useRef<ReturnType<typeof setInterval> | null>(null);
    const [subjectId, setSubjectId] = useState('');

    // --- Batch state ---
    const [batchFiles, setBatchFiles] = useState<BatchFileState[]>([]);
    const [batchUploading, setBatchUploading] = useState(false);
    const [batchDone, setBatchDone] = useState(false);

    useEffect(() => {
        dispatch(resetUpload());
        return () => {
            dispatch(resetUpload());
            // Zip upload (when restored): if (zipPollRef.current) clearInterval(zipPollRef.current);
        };
    }, [dispatch]);

    // --- Single file handlers ---
    const handleSelect = useCallback((f: File) => {
        const err = validateHDF5(f);
        setValidationError(err);
        if (!err) setFile(f); else setFile(null);
    }, []);

    const handleSubmit = useCallback(() => {
        if (!file) return;
        dispatch(resetUpload());
        dispatch(submitHDF5Upload({ file, subjectId: subjectId.trim() || undefined }));
    }, [dispatch, file, subjectId]);

    useEffect(() => {
        if (!upload.jobId || upload.jobStatus === 'SUCCEEDED' || upload.jobStatus === 'FAILED') {
            if (pollRef.current) clearInterval(pollRef.current);
            return;
        }
        pollRef.current = setInterval(() => { dispatch(pollJobStatus(upload.jobId!)); }, 3000);
        return () => { if (pollRef.current) clearInterval(pollRef.current); };
    }, [dispatch, upload.jobId, upload.jobStatus]);

    const isSubmitting = upload.loading;
    const isPolling = !!upload.jobId && upload.jobStatus !== 'SUCCEEDED' && upload.jobStatus !== 'FAILED';
    const succeeded = upload.jobStatus === 'SUCCEEDED';
    const failed = upload.jobStatus === 'FAILED';

    // --- Batch handlers ---
    const handleBatchSelect = useCallback((files: File[]) => {
        const entries: BatchFileState[] = files.map(f => ({
            file: f,
            status: 'pending' as const,
            jobId: null,
            error: null,
            validationError: validateHDF5(f),
        }));
        setBatchFiles(prev => [...prev, ...entries]);
        setBatchDone(false);
    }, []);

    const removeBatchFile = useCallback((idx: number) => {
        setBatchFiles(prev => prev.filter((_, i) => i !== idx));
    }, []);

    const handleBatchUpload = useCallback(async () => {
        setBatchUploading(true);
        setBatchDone(false);
        const updated = [...batchFiles];

        for (let i = 0; i < updated.length; i++) {
            const entry = updated[i];
            if (entry.validationError) {
                updated[i] = { ...entry, status: 'failed', error: entry.validationError };
                setBatchFiles([...updated]);
                continue;
            }

            updated[i] = { ...entry, status: 'uploading' };
            setBatchFiles([...updated]);

            try {
                const presignRes = await presignUpload({ filename: entry.file.name });
                const presignedUrl = presignRes?.presigned_url;
                const s3Key = presignRes?.s3_key;
                const fileUuid = presignRes?.file_uuid;

                await fetch(presignedUrl, { method: 'PUT', body: entry.file });

                const jobRes = await submitJob({ s3_key: s3Key, uuid: fileUuid });
                updated[i] = { ...entry, status: 'submitted', jobId: jobRes.job_id };
            } catch (err: any) {
                updated[i] = { ...entry, status: 'failed', error: err?.message || 'Upload failed' };
            }
            setBatchFiles([...updated]);
        }

        setBatchUploading(false);
        setBatchDone(true);
    }, [batchFiles]);

    const batchCounts = {
        total: batchFiles.length,
        submitted: batchFiles.filter(f => f.status === 'submitted').length,
        failed: batchFiles.filter(f => f.status === 'failed').length,
        pending: batchFiles.filter(f => f.status === 'pending').length,
    };

    /*
     * --------------------------------------------------------------------------
     * Zip upload (Batch tab) — commented out; will be implemented soon.
     * Restore: uncomment imports + MAX_ZIP_SIZE, this block, useEffect cleanup above,
     * and the JSX block after the Multi-File Upload card (search "Zip upload section").
     * --------------------------------------------------------------------------
    const [zipFile, setZipFile] = useState<File | null>(null);
    const [zipStatus, setZipStatus] = useState<'idle' | 'uploading' | 'extracting' | 'done' | 'error'>('idle');
    const [zipResult, setZipResult] = useState<any>(null);
    const [zipError, setZipError] = useState<string | null>(null);
    const zipPollRef = useRef<ReturnType<typeof setInterval> | null>(null);

    const handleZipSelect = useCallback((f: File) => {
        if (!f.name.toLowerCase().endsWith('.zip')) {
            setZipError('Only .zip files are accepted');
            return;
        }
        if (f.size > MAX_ZIP_SIZE) {
            setZipError(`Zip too large (${(f.size / (1024 ** 3)).toFixed(1)} GB). Max 5 GB.`);
            return;
        }
        setZipFile(f);
        setZipError(null);
        setZipStatus('idle');
        setZipResult(null);
    }, []);

    const handleZipUpload = useCallback(async () => {
        if (!zipFile) return;
        setZipStatus('uploading');
        setZipError(null);

        try {
            const presignRes = await presignUpload({ filename: zipFile.name, file_size: zipFile.size });
            const presignedUrl = presignRes?.presigned_url;
            const s3Key = presignRes?.s3_key;

            await fetch(presignedUrl, { method: 'PUT', body: zipFile });

            setZipStatus('extracting');
            const ingestRes = await ingestZip(s3Key);
            const ingestionId = ingestRes?.ingestion_id;

            if (zipPollRef.current) clearInterval(zipPollRef.current);
            zipPollRef.current = setInterval(async () => {
                try {
                    const statusRes = await getIngestionStatus(ingestionId);
                    setZipResult(statusRes);
                    if (statusRes.status === 'COMPLETED' || statusRes.status === 'FAILED') {
                        if (zipPollRef.current) clearInterval(zipPollRef.current);
                        zipPollRef.current = null;
                        setZipStatus(statusRes.status === 'COMPLETED' ? 'done' : 'error');
                        if (statusRes.status === 'FAILED') setZipError(statusRes.error_message);
                    }
                } catch {
                    if (zipPollRef.current) clearInterval(zipPollRef.current);
                    zipPollRef.current = null;
                    setZipStatus('error');
                }
            }, 3000);
        } catch (err: any) {
            setZipStatus('error');
            setZipError(err?.message || 'Zip upload failed');
        }
    }, [zipFile]);
    */

    return (
        <DashboardWrapper>
            <Container>
                <BackButton onClick={() => navigate('/retina')}>&larr; Back to Dashboard</BackButton>

                <div>
                    <Text fontSize={Size.Large} fontWeight="600" color={Colors.extra.black}>Upload Scan</Text>
                    <Text fontSize={Size.Small} color={Colors.extra.dark_gray} marginTop={8}>
                        Upload HDF5 files for analysis
                    </Text>
                </div>

                <TabBar>
                    <Tab $active={activeTab === 'single'} onClick={() => setActiveTab('single')}>Single File</Tab>
                    <Tab $active={activeTab === 'batch'} onClick={() => setActiveTab('batch')}>Batch Upload</Tab>
                </TabBar>

                {/* ============ SINGLE FILE TAB ============ */}
                {activeTab === 'single' && (
                    <UploadCard>
                        <UploadZone
                            $dragOver={dragOver}
                            onDragOver={e => { e.preventDefault(); setDragOver(true); }}
                            onDragLeave={() => setDragOver(false)}
                            onDrop={e => { e.preventDefault(); setDragOver(false); const f = e.dataTransfer.files[0]; if (f) handleSelect(f); }}
                            onClick={() => document.getElementById('hdf5-input')?.click()}
                        >
                            <Text fontWeight="500" color={Colors.extra.black}>
                                {file ? file.name : 'Click or drag HDF5 file here'}
                            </Text>
                            <Text fontSize={Size.XSmall} color={Colors.extra.dark_gray} marginTop={8}>
                                Single HDF5 file (.h5, .hdf5), max 4 GB
                            </Text>
                            <input id="hdf5-input" type="file" accept=".h5,.hdf5" style={{ display: 'none' }}
                                onChange={e => { const f = e.target.files?.[0]; if (f) handleSelect(f); e.target.value = ''; }} />
                        </UploadZone>
                        <Constraints><span>Format: HDF5</span><span>Max: 4 GB</span></Constraints>

                        {validationError && <ErrorBox>{validationError}</ErrorBox>}

                        {file && !validationError && (
                            <FileRow>
                                <div><FileName>{file.name}</FileName><FileSizeLabel>{fmt(file.size)}</FileSizeLabel></div>
                                {!isSubmitting && !isPolling && !succeeded && <RemoveBtn onClick={() => setFile(null)}>Remove</RemoveBtn>}
                            </FileRow>
                        )}

                        {file && !validationError && !isSubmitting && !isPolling && !succeeded && (
                            <div style={{ marginTop: 12 }}>
                                <FileSizeLabel>Subject ID (optional)</FileSizeLabel>
                                <input
                                    type="text"
                                    placeholder="e.g. CLT123"
                                    value={subjectId}
                                    onChange={e => setSubjectId(e.target.value)}
                                    style={{
                                        width: '100%', padding: '8px 12px', marginTop: 4,
                                        border: `1px solid ${Colors.extra.light_gray}`, borderRadius: 8,
                                        fontSize: 13, outline: 'none',
                                    }}
                                />
                                {subjectId.trim() && (
                                    <FileSizeLabel style={{ marginTop: 4 }}>
                                        This will create or link to patient: {subjectId.trim().toLowerCase()}
                                    </FileSizeLabel>
                                )}
                            </div>
                        )}

                        {(isSubmitting || isPolling) && (
                            <div style={{ marginTop: 16, display: 'flex', flexDirection: 'column', gap: 8 }}>
                                <ProgressBarBg><ProgressBarFill $pct={upload.jobProgress} /></ProgressBarBg>
                                <Text fontSize={Size.XSmall} color={Colors.extra.dark_gray}>
                                    {upload.jobStatus ? `${upload.jobStatus} (${upload.jobProgress}%)` : 'Uploading…'}
                                </Text>
                            </div>
                        )}

                        {succeeded && (
                            <SuccessBox>
                                <Text fontWeight="500">Analysis completed.</Text>
                                <div style={{ display: 'flex', gap: 8 }}>
                                    <Button onClick={() => navigate('/scans')}>View Scans</Button>
                                    <Button onClick={() => { dispatch(resetUpload()); setFile(null); }}>Upload Another</Button>
                                </div>
                            </SuccessBox>
                        )}
                        {failed && <ErrorBox>Analysis failed: {upload.jobError || 'Unknown error'}</ErrorBox>}
                        {upload.error && !failed && <ErrorBox>Upload error: {upload.error}</ErrorBox>}

                        {file && !validationError && !isSubmitting && !isPolling && !succeeded && (
                            <Button onClick={handleSubmit} margin="16px 0 0 0" width="100%">Upload &amp; Analyze</Button>
                        )}
                    </UploadCard>
                )}

                {/* ============ BATCH UPLOAD TAB ============ */}
                {activeTab === 'batch' && (
                    <>
                    <UploadCard>
                        <SectionTitle>Multi-File Upload</SectionTitle>
                        <UploadZone onClick={() => document.getElementById('batch-input')?.click()}>
                            <Text fontWeight="500" color={Colors.extra.black}>Select multiple HDF5 files</Text>
                            <Text fontSize={Size.XSmall} color={Colors.extra.dark_gray} marginTop={8}>
                                .h5 / .hdf5 files, max 4 GB each
                            </Text>
                            <input id="batch-input" type="file" multiple accept=".h5,.hdf5" style={{ display: 'none' }}
                                onChange={e => { handleBatchSelect(Array.from(e.target.files || [])); e.target.value = ''; }} />
                        </UploadZone>

                        {batchFiles.length > 0 && (
                            <div style={{ marginTop: 12 }}>
                                {batchFiles.map((entry, i) => (
                                    <FileRow key={i}>
                                        <div style={{ flex: 1, minWidth: 0 }}>
                                            <FileName>{entry.file.name}</FileName>
                                            <FileSizeLabel>
                                                {fmt(entry.file.size)}
                                                {entry.validationError && <StatusLabel $color="#DC2626"> — {entry.validationError}</StatusLabel>}
                                            </FileSizeLabel>
                                        </div>
                                        <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                                            {entry.status === 'pending' && !batchUploading && (
                                                <RemoveBtn onClick={() => removeBatchFile(i)}>Remove</RemoveBtn>
                                            )}
                                            {entry.status === 'uploading' && <StatusLabel $color="#2563EB">Uploading…</StatusLabel>}
                                            {entry.status === 'submitted' && <StatusLabel $color="#059669">Submitted</StatusLabel>}
                                            {entry.status === 'failed' && <StatusLabel $color="#DC2626">Failed</StatusLabel>}
                                        </div>
                                    </FileRow>
                                ))}

                                {batchDone && (
                                    <Summary>
                                        {batchCounts.submitted} uploaded, {batchCounts.failed} failed
                                    </Summary>
                                )}

                                <div style={{ display: 'flex', gap: 8, marginTop: 16 }}>
                                    {!batchUploading && !batchDone && batchFiles.some(f => !f.validationError) && (
                                        <Button onClick={handleBatchUpload} width="100%">
                                            Upload All ({batchFiles.filter(f => !f.validationError).length} files)
                                        </Button>
                                    )}
                                    {batchDone && (
                                        <>
                                            <Button onClick={() => navigate('/scans')}>View Scans</Button>
                                            <Button onClick={() => { setBatchFiles([]); setBatchDone(false); }}>Upload More</Button>
                                        </>
                                    )}
                                </div>
                            </div>
                        )}
                    </UploadCard>

                    {/*
                      Zip upload section — will be implemented soon (see commented hooks above).
                      Restore: wrap batch tab in <>...</> and place this card after Multi-File Upload.

                    <UploadCard>
                        <SectionTitle>Zip Upload</SectionTitle>
                        <UploadZone onClick={() => document.getElementById('zip-input')?.click()}>
                            <Text fontWeight="500" color={Colors.extra.black}>
                                {zipFile ? zipFile.name : 'Select a .zip containing HDF5 files'}
                            </Text>
                            <Text fontSize={Size.XSmall} color={Colors.extra.dark_gray} marginTop={8}>
                                Max 5 GB. For larger batches, use multi-file upload above.
                            </Text>
                            <input id="zip-input" type="file" accept=".zip" style={{ display: 'none' }}
                                onChange={e => { const f = e.target.files?.[0]; if (f) handleZipSelect(f); e.target.value = ''; }} />
                        </UploadZone>

                        {zipFile && zipStatus === 'idle' && !zipError && (
                            <div style={{ marginTop: 12 }}>
                                <FileRow>
                                    <div><FileName>{zipFile.name}</FileName><FileSizeLabel>{fmt(zipFile.size)}</FileSizeLabel></div>
                                    <RemoveBtn onClick={() => { setZipFile(null); setZipError(null); }}>Remove</RemoveBtn>
                                </FileRow>
                                <Button onClick={handleZipUpload} margin="12px 0 0 0" width="100%">Upload &amp; Extract</Button>
                            </div>
                        )}

                        {zipStatus === 'uploading' && (
                            <Summary>Uploading zip to S3…</Summary>
                        )}

                        {zipStatus === 'extracting' && zipResult && (
                            <Summary>
                                Extracting… {zipResult.processed_files ?? 0}/{zipResult.total_files ?? '?'} files processed
                            </Summary>
                        )}

                        {zipStatus === 'done' && zipResult && (
                            <SuccessBox>
                                <Text fontWeight="500">
                                    {zipResult.total_files} files found, {zipResult.jobs_created} scans queued
                                    {zipResult.jobs_failed > 0 && `, ${zipResult.jobs_failed} failed`}
                                    {zipResult.files_skipped > 0 && `, ${zipResult.files_skipped} skipped`}
                                </Text>
                                <Button onClick={() => navigate('/scans')}>View Scans</Button>
                            </SuccessBox>
                        )}

                        {zipError && <ErrorBox>{zipError}</ErrorBox>}
                    </UploadCard>
                    */}
                    </>
                )}
            </Container>
        </DashboardWrapper>
    );
};

export default UploadPage;
